import { SystemInlineBanner, Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import {
  convertNumberToWord,
  isNumberConvertableToWord,
} from "@src/appV2/lib/utils/convertNumberToWord";
import { Button } from "@src/appV2/redesign/components/Button";
import pluralize from "pluralize";

import { PageFooterWrapper } from "../../../../../components/PageFooterWrapper";
import { type ProfessionalReferenceStatus } from "../types";
interface ReferenceFooterProps {
  minRequired: number;
  referencesCountsByStatus: Record<ProfessionalReferenceStatus, number>;
  onAddReference: () => void;
}

function getSystemInlineBanner(
  referencesCountsByStatus: Record<ProfessionalReferenceStatus, number>,
  minRequired: number
) {
  const {
    requested: requestedCount,
    declined: declinedCount,
    verified: verifiedCount,
  } = referencesCountsByStatus;

  const requestedAndVerifiedCount = requestedCount + verifiedCount;

  const hasDeclinedMoreThanRequested = declinedCount > requestedCount;
  const verifiedLessThanRequired = verifiedCount < minRequired;
  const notEnoughRequestedOrVerified = requestedAndVerifiedCount < minRequired;

  const minRequiredWord = isNumberConvertableToWord(minRequired)
    ? convertNumberToWord(minRequired)
    : minRequired.toString();

  if (
    (hasDeclinedMoreThanRequested && verifiedLessThanRequired) ||
    (declinedCount > 0 && notEnoughRequestedOrVerified)
  ) {
    const missingRequired = minRequired - requestedAndVerifiedCount;
    const missingRequiredWord = isNumberConvertableToWord(missingRequired)
      ? convertNumberToWord(missingRequired)
      : missingRequired.toString();

    return {
      color: "warning",
      text: `You need ${minRequired} approved references. Please add ${missingRequiredWord} more to complete this step.`,
    } as const;
  }

  return {
    color: "neutral",
    text: `We only require ${minRequiredWord} ${pluralize(
      "reference",
      minRequired
    )}, but you may add more.`,
  } as const;
}

export function ReferenceFooter(props: ReferenceFooterProps) {
  const { minRequired, referencesCountsByStatus, onAddReference } = props;

  const { text: systemInlineBannerText, color: systemInlineBannerColor } = getSystemInlineBanner(
    referencesCountsByStatus,
    minRequired
  );

  return (
    <PageFooterWrapper>
      <Stack gap={4}>
        <SystemInlineBanner iconType="triangle-exclamation" color={systemInlineBannerColor}>
          <Text inline variant="body1" color="inherit">
            {systemInlineBannerText}
          </Text>
        </SystemInlineBanner>
        <Button
          fullWidth
          size="large"
          variant="outlined"
          startIconType="plus"
          onClick={onAddReference}
        >
          Add Reference
        </Button>
      </Stack>
    </PageFooterWrapper>
  );
}
